/**
 * Session Replay Component
 * Shows a read-only review of the completed sourcing session
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import {
    FileText,
    Search,
    Users,
    MessageSquare,
    Briefcase,
    Building2,
    MapPin,
    Clock,
    CheckCircle,
    XCircle,
    ThumbsUp,
    ThumbsDown,
    HelpCircle,
    Star,
    Mic,
    ChevronRight,
    Eye
} from 'lucide-react';

const SessionReplay = ({ session, open, onClose }) => {
    const [activeTab, setActiveTab] = useState('job');
    
    if (!session) return null;
    
    const jobBrief = session.job_brief || {};
    const searchString = session.selected_search_string || {};
    const evaluations = session.evaluations || {};
    const candidates = session.candidate_pool || [];
    const aiMessages = session.ai_messages || [];

    const getVerdictBadge = (verdict) => {
        switch (verdict) {
            case 'good_fit':
                return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">Good Fit</Badge>;
            case 'partial_fit':
                return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">Partial Fit</Badge>;
            case 'not_a_fit':
                return <Badge className="bg-red-500/10 text-red-600 border-red-500/30">Not a Fit</Badge>;
            default:
                return <Badge variant="outline">Unknown</Badge>;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[85vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-primary" />
                        Session Replay
                    </DialogTitle>
                    <DialogDescription>
                        Review your decisions and reasoning from this sourcing session
                    </DialogDescription>
                </DialogHeader>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                    <TabsList className="grid grid-cols-4 w-full">
                        <TabsTrigger value="job" className="flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            <span className="hidden sm:inline">Job Brief</span>
                        </TabsTrigger>
                        <TabsTrigger value="search" className="flex items-center gap-1">
                            <Search className="h-4 w-4" />
                            <span className="hidden sm:inline">Search</span>
                        </TabsTrigger>
                        <TabsTrigger value="candidates" className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span className="hidden sm:inline">Candidates</span>
                        </TabsTrigger>
                        <TabsTrigger value="discussion" className="flex items-center gap-1">
                            <MessageSquare className="h-4 w-4" />
                            <span className="hidden sm:inline">AI Chat</span>
                        </TabsTrigger>
                    </TabsList>

                    <ScrollArea className="h-[55vh] mt-4">
                        {/* Job Brief Tab */}
                        <TabsContent value="job" className="space-y-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Briefcase className="h-5 w-5 text-primary" />
                                        {jobBrief.title}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-3 gap-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            <Building2 className="h-4 w-4 text-muted-foreground" />
                                            {jobBrief.industry}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                            {jobBrief.experience_range?.min}-{jobBrief.experience_range?.max} years
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 text-muted-foreground" />
                                            {jobBrief.location}
                                        </div>
                                    </div>
                                    
                                    <Separator />
                                    
                                    <div>
                                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                                            <Star className="h-4 w-4 text-amber-500" />
                                            Required Skills
                                        </h4>
                                        <div className="flex flex-wrap gap-1">
                                            {jobBrief.required_skills?.map((skill, idx) => (
                                                <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                            <h4 className="font-semibold mb-2 text-sm text-emerald-600 flex items-center gap-1">
                                                <CheckCircle className="h-4 w-4" />
                                                Ideal Background
                                            </h4>
                                            <ul className="space-y-1 text-xs">
                                                {jobBrief.ideal_background?.slice(0, 3).map((bg, idx) => (
                                                    <li key={idx}>• {bg}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                                            <h4 className="font-semibold mb-2 text-sm text-red-600 flex items-center gap-1">
                                                <XCircle className="h-4 w-4" />
                                                Red Flags
                                            </h4>
                                            <ul className="space-y-1 text-xs">
                                                {jobBrief.red_flag_backgrounds?.slice(0, 3).map((bg, idx) => (
                                                    <li key={idx}>• {bg}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Search Strategy Tab */}
                        <TabsContent value="search" className="space-y-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Search className="h-5 w-5 text-amber-500" />
                                        Your Search String
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {searchString.query ? (
                                        <>
                                            <div className="p-4 rounded-lg bg-muted/50 font-mono text-sm">
                                                {searchString.query}
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-semibold mb-2 text-sm">Target Platforms</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {searchString.platforms?.map((platform, idx) => (
                                                        <Badge key={idx} variant="outline">{platform}</Badge>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {searchString.reasoning && (
                                                <div className="p-3 rounded-lg border bg-muted/20">
                                                    <h4 className="font-semibold mb-2 text-sm flex items-center gap-2">
                                                        <Mic className="h-4 w-4 text-primary" />
                                                        Your Reasoning (Voice)
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">{searchString.reasoning}</p>
                                                </div>
                                            )}
                                            
                                            {searchString.evaluation && (
                                                <div className="p-3 rounded-lg border">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-semibold text-sm">AI Evaluation</h4>
                                                        <Badge variant={searchString.evaluation.score >= 70 ? "default" : "destructive"}>
                                                            Score: {searchString.evaluation.score}%
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">{searchString.evaluation.feedback}</p>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-muted-foreground text-center py-8">No search string submitted</p>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Candidates Tab */}
                        <TabsContent value="candidates" className="space-y-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Users className="h-5 w-5 text-emerald-500" />
                                        Candidate Evaluations
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {candidates.length > 0 ? (
                                        candidates.map((candidate, idx) => {
                                            const evaluation = evaluations[candidate.id];
                                            return (
                                                <div key={idx} className="p-3 rounded-lg border">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div>
                                                            <p className="font-semibold">{candidate.name}</p>
                                                            <p className="text-sm text-muted-foreground">
                                                                {candidate.current_title} at {candidate.current_company}
                                                            </p>
                                                        </div>
                                                        {evaluation ? (
                                                            getVerdictBadge(evaluation.fit_status)
                                                        ) : (
                                                            <Badge variant="outline">Not Evaluated</Badge>
                                                        )}
                                                    </div>
                                                    {evaluation?.reasoning && (
                                                        <div className="mt-2 p-2 rounded bg-muted/30 text-sm">
                                                            <span className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                                                                <Mic className="h-3 w-3" />
                                                                Your reasoning:
                                                            </span>
                                                            {evaluation.reasoning}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="text-muted-foreground text-center py-8">No candidates in this session</p>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* AI Discussion Tab */}
                        <TabsContent value="discussion" className="space-y-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <MessageSquare className="h-5 w-5 text-purple-500" />
                                        AI Mentor Discussion
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {aiMessages.length > 0 ? (
                                        <div className="space-y-3">
                                            {aiMessages.map((msg, idx) => (
                                                <div 
                                                    key={idx}
                                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div className={`max-w-[85%] p-3 rounded-xl ${
                                                        msg.role === 'user' 
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'bg-muted'
                                                    }`}>
                                                        {msg.role === 'user' && (
                                                            <div className="flex items-center gap-1 mb-1 opacity-70">
                                                                <Mic className="h-3 w-3" />
                                                                <span className="text-xs">You said:</span>
                                                            </div>
                                                        )}
                                                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted-foreground text-center py-8">No AI discussion recorded</p>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </ScrollArea>
                </Tabs>
                
                <div className="flex justify-end pt-4 border-t">
                    <Button onClick={onClose}>Close</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SessionReplay;
